/**
 * The viewer's Layers menu: a top-left dropdown (beside the measure / staging tools) for the
 * region layers visitors switch on and off (RegionsRuntime.layersMenu: the regions with
 * `inLayersMenu`), with their colour swatches, optionally under the creator's headings (a
 * collapsible sub-menu each), plus the "Show all layers" entry. Clicks go to the runtime, which
 * applies the selection mode. (Measurement sets are picked in the Measure panel instead:
 * measurementSetsPicker.ts.)
 *
 * The panel re-renders checked states from every `onChange`, and `rebuild()` re-reads names,
 * colours and settings after an edit. Built with DOM APIs only (no innerHTML of authored text), so
 * names can't inject markup.
 */
import type { LayersMenuSource } from './regionsRuntime';

export interface LayersPanelOptions {
    runtime: LayersMenuSource;
    /** Toolbar button that opens / closes the panel. */
    button: HTMLElement;
    /** Empty container the panel is rendered into (positioned by the viewer's stylesheet). */
    panel: HTMLElement;
    /** Formats a layer's total area (scene units²) for display, e.g. "42.3 m²". */
    formatArea?: (rawArea: number) => string;
    /** Called when the panel opens, so the viewer can close the other top-left tools. */
    onOpen?: () => void;
}

export interface LayersPanelHandle {
    setOpen(open: boolean): void;
    isOpen(): boolean;
    /** Re-read layer names / colours / areas (the runtime's config changed). */
    rebuild(): void;
    destroy(): void;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
};

export function mountLayersPanel(options: LayersPanelOptions): LayersPanelHandle {
    const { runtime, button, panel } = options;
    let open = false;
    const collapsed = new Set<string>();
    const items = new Map<string, HTMLButtonElement>();
    let allItem: HTMLButtonElement | null = null;

    panel.setAttribute('role', 'dialog');

    const refreshChecks = () => {
        const single = runtime.panel.selectionMode === 'single';
        const all = runtime.allVisible();
        for (const [id, item] of items) {
            // In single mode "All" stands in for every layer, so the rows read as one choice.
            const checked = runtime.isVisible(id) && !(single && all && items.size > 1);
            item.setAttribute('aria-checked', checked ? 'true' : 'false');
            item.classList.toggle('is-on', runtime.isVisible(id));
        }
        if (allItem) {
            allItem.setAttribute('aria-checked', all ? 'true' : 'false');
            allItem.classList.toggle('is-on', all);
        }
    };

    /** A collapsible heading (one of the creator's) and its layers. */
    const buildSection = (key: string, name: string, fill: (body: HTMLElement) => void): HTMLElement => {
        const wrap = el('div', 'storysplat-layers-section');
        const head = el('button', 'storysplat-layers-section-header');
        head.type = 'button';
        head.appendChild(el('span', 'storysplat-layers-section-name', name));
        head.appendChild(el('span', 'storysplat-layers-chevron'));
        const body = el('div', 'storysplat-layers-section-items');
        const applyCollapsed = () => {
            const isCollapsed = collapsed.has(key);
            wrap.classList.toggle('collapsed', isCollapsed);
            head.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
        };
        head.addEventListener('click', (e) => {
            e.stopPropagation();
            if (collapsed.has(key)) collapsed.delete(key);
            else collapsed.add(key);
            applyCollapsed();
        });
        applyCollapsed();
        fill(body);
        wrap.append(head, body);
        return wrap;
    };

    const buildItem = (group: ReturnType<LayersMenuSource['getGroups']>[number], role: string): HTMLButtonElement => {
        const item = el('button', 'storysplat-layers-item');
        item.type = 'button';
        item.setAttribute('role', role);
        item.dataset.layerId = group.id;
        const swatch = el('span', 'storysplat-layers-swatch');
        swatch.style.setProperty('--storysplat-layer-color', group.color);
        swatch.setAttribute('aria-hidden', 'true');
        const textWrap = el('span', 'storysplat-layers-text');
        textWrap.appendChild(el('span', 'storysplat-layers-name', group.name));
        if (group.description) textWrap.appendChild(el('span', 'storysplat-layers-desc', group.description));
        item.append(swatch, textWrap);
        if (group.showArea && group.area > 0 && options.formatArea) {
            item.appendChild(el('span', 'storysplat-layers-area', options.formatArea(group.area)));
        }
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            runtime.select(group.id);
        });
        items.set(group.id, item);
        return item;
    };

    const rebuild = () => {
        panel.replaceChildren();
        items.clear();
        allItem = null;
        const cfg = runtime.panel;
        const role = cfg.selectionMode === 'single' ? 'menuitemradio' : 'menuitemcheckbox';
        panel.setAttribute('aria-label', cfg.title);
        button.setAttribute('aria-label', cfg.title);
        button.setAttribute('title', cfg.title);

        const header = el('div', 'storysplat-layers-header');
        header.appendChild(el('span', 'storysplat-layers-title', cfg.title));
        const close = el('button', 'storysplat-layers-close', '×');
        close.type = 'button';
        close.setAttribute('aria-label', 'Close');
        close.addEventListener('click', (e) => {
            e.stopPropagation();
            setOpen(false);
        });
        header.appendChild(close);
        panel.appendChild(header);

        const list = el('div', 'storysplat-layers-list');
        list.setAttribute('role', 'menu');
        const groups = runtime.getGroups();
        if (cfg.showAllOption && groups.length > 1) {
            allItem = el('button', 'storysplat-layers-item storysplat-layers-all');
            allItem.type = 'button';
            allItem.setAttribute('role', role);
            const swatch = el('span', 'storysplat-layers-swatch storysplat-layers-swatch--all');
            swatch.setAttribute('aria-hidden', 'true');
            // A small stripe of every layer colour.
            swatch.style.setProperty(
                '--storysplat-layer-color',
                `linear-gradient(90deg, ${groups.map((g, i) => `${g.color} ${(i / groups.length) * 100}% ${((i + 1) / groups.length) * 100}%`).join(', ')})`,
            );
            const textWrap = el('span', 'storysplat-layers-text');
            textWrap.appendChild(el('span', 'storysplat-layers-name', cfg.allLabel));
            allItem.append(swatch, textWrap);
            allItem.addEventListener('click', (e) => {
                e.stopPropagation();
                runtime.selectAll();
            });
            list.appendChild(allItem);
        }
        // Layers without a heading first, then each heading in order of first appearance.
        const headings: string[] = [];
        for (const g of groups) if (g.section && !headings.includes(g.section)) headings.push(g.section);
        for (const g of groups) if (!g.section) list.appendChild(buildItem(g, role));
        for (const name of headings) {
            list.appendChild(buildSection(name, name, (body) => {
                for (const g of groups) if (g.section === name) body.appendChild(buildItem(g, role));
            }));
        }
        panel.appendChild(list);
        refreshChecks();
    };

    const setOpen = (next: boolean) => {
        if (next === open) return;
        open = next;
        panel.classList.toggle('open', open);
        button.classList.toggle('active', open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) options.onOpen?.();
    };

    const onButtonClick = (e: Event) => {
        e.stopPropagation();
        setOpen(!open);
    };
    button.setAttribute('aria-haspopup', 'true');
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', onButtonClick);
    // Clicks inside the panel must not reach the canvas (they'd refocus or move the camera).
    const stop = (e: Event) => e.stopPropagation();
    panel.addEventListener('pointerdown', stop);
    panel.addEventListener('click', stop);
    panel.addEventListener('wheel', stop, { passive: true });
    const unsubscribe = runtime.onChange(refreshChecks);

    rebuild();
    if (runtime.panel.startOpen) setOpen(true);

    return {
        setOpen,
        isOpen: () => open,
        rebuild,
        destroy: () => {
            unsubscribe();
            button.removeEventListener('click', onButtonClick);
            panel.removeEventListener('pointerdown', stop);
            panel.removeEventListener('click', stop);
            panel.removeEventListener('wheel', stop);
            panel.remove();
            button.remove();
        },
    };
}
